// Sign-up moments: ask at the moment of value (#5195; founder pick A of the
// mocks, 2026-10-23, https://claude.ai/artifact/CyKUdSiz1xn7WfC7hegycL).
// Spec: docs/specs/signup-moments/ (product.md, design.md).
//
// Each moment sits right under what a signed-out visitor just got: one free
// line (the taste), what a free account adds as a short list, and one button
// that names it. The sign-in sheet then repeats the button's words.
//
// The moments are a registry (one entry per moment, never a branch at a call
// site): its sign-in entry, and how its box is worded from the data the page
// holds. A new moment is one entry here.
//
// What a box may say is only what the code computes for a guest (the spec's
// feasibility rules, product invariants 14, 16, 17): the band and rate the
// free read already shows, the band at every GPA from the server's sweep
// (signup_moments.fixed_band), the Targets count and the first one's name
// (signup_moments.targets). "Why {school} is a {band}" is an ITEM, behind
// sign-in, and only when the server's locked read holds reasons (its unlock
// box came back); the reasons themselves never reach a guest. No credit,
// usage or limit word anywhere (invariant 1). Keep this module import-free:
// the drift tests read it from the source tree.

// The moment ids, one list with the server's closed list
// (cloud_functions/profile_manager_v2/app_events.py SIGNUP_MOMENTS, pinned by
// tests/cloud_functions/profile_manager_v2/test_signup_moments.py).
export const MOMENT_IDS = ['chances_result', 'astra_read', 'long_read', 'comparing'];
export const STATES = ['shown', 'clicked', 'dismissed', 'followed'];
export const PAYOFF_KINDS = ['targets', 'why', 'both', 'bands', 'figure'];

export const COPY = {
    fixedBand: (school, band, admits) => `${school} reads ${band} at every GPA.${admits ? ` It ${admits}.` : ''}`,
    heading: (gpa) => `Where your ${gpa} reads Target`,
    headingNoTargets: 'With a free account',
    freeLine: (n, name) => (n === 1
        ? `Target at 1 college we cover: ${name}.`
        : `Target at ${n} colleges we cover${name ? `, like ${name}` : ''}.`),
    listLabel: 'With a free account',
    allTargets: (n) => `All ${n}, with why each one is a Target`,
    oneTarget: (name) => `Why ${name} is a Target`,
    why: (school, band) => `Why ${school} is a ${band}`,
    savedReminder: (school, date) => `${school} saved, with a reminder before ${date}`,
    savedList: (school) => `${school} saved to your list`,
    conversation: 'This conversation comes with you',
    button: (n) => (n === 1 ? 'See my Target' : `See my ${n} Targets`),
    buttonWhy: (band) => `See why it's a ${band}`,
    notNow: 'Not now',
    notNowAsking: 'Not now, keep asking',
    // the long read (a college page, halfway down)
    longRead: (school, admitted, applied, year) => `${school} admitted ${admitted} of ${applied} applicants for ${year}. Where would you be?`,
    longReadGo: 'Check with my grades',
    longReadSmall: 'Free, no sign-in. Takes your grade, GPA and state.',
    // comparing (the second college page in a visit)
    stripLabel: 'Your colleges',
    stripTargets: (gpa, n) => `Your ${gpa} reads Target at ${n} ${n === 1 ? 'college' : 'colleges'} we cover.`,
    stripSave: 'Save these to my list',
    stripCheck: 'Check with my grades',
    stripLead: (names) => `${names} will be ready to add on your first screen. Free.`,
    // after sign-up, on /start
    seeTargets: 'See your Targets',
};

/** "a, b and c". */
export function joinAnd(parts) {
    const p = parts.filter(Boolean);
    if (p.length <= 1) return p[0] || '';
    return `${p.slice(0, -1).join(', ')} and ${p[p.length - 1]}`;
}

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/**
 * The rate phrase from the read's own free line (astra_read_unlock.band_line,
 * "Michigan admits about 1 in 6. A Reach means..."): "admits about 1 in 6",
 * or null. Read from the server's words, never re-derived here, so the box
 * and the read card can never quote two different rates.
 */
export function admitsPhrase(bandLine) {
    const m = /^.+? (admits [^.]+)\./.exec(String(bandLine || ''));
    return m ? m[1] : null;
}

/** The bucket a Targets count rides an event in (never the count itself). */
export function targetsBucket(n) {
    if (!Number.isInteger(n) || n < 0) return null;
    if (n === 0) return '0';
    if (n <= 5) return '1-5';
    if (n <= 20) return '6-20';
    return '21+';
}

/**
 * The box under a read (chances_result and astra_read). `d`:
 *   school   the short name the page shows ("Michigan")
 *   band     the read's band (Safety | Target | Reach)
 *   gpa      the GPA the visitor typed, as they typed it ("3.7"), or null
 *   targets  {n, sample: {id, name}} from the server, or null (not known)
 *   why      the server's locked read holds reasons (its unlock box came back)
 *   date     the next dated deadline ("Nov 1") or null
 *   early    9th or 10th grade: no reminder, "saved to your list"
 *   saved    the school is carried onto the list at sign-in (the #3480 carry)
 *   thread   the conversation has a question in it
 * Returns {heading, free, items, button, lead, payoff} or null when the box
 * has nothing true to offer (then the page keeps its old prompt).
 */
function readBox(d) {
    const n = d.targets && Number.isInteger(d.targets.n) ? d.targets.n : null;
    const sample = n && d.targets.sample ? d.targets.sample.name : null;
    const hasTargets = n !== null && n > 0;
    const items = [];
    const lead = [];
    if (hasTargets && n > 1) { items.push(COPY.allTargets(n)); lead.push(`all ${n}`); }
    if (hasTargets && n === 1 && sample) { items.push(COPY.oneTarget(sample)); lead.push(`why ${sample} is a Target`); }
    if (d.why && d.band) { items.push(COPY.why(d.school, d.band)); lead.push(`why ${d.school} is a ${d.band}`); }
    if (d.saved) {
        if (d.date && !d.early) {
            items.push(COPY.savedReminder(d.school, d.date));
            lead.push(`a reminder before ${d.date}`);
        } else {
            items.push(COPY.savedList(d.school));
            lead.push(`${d.school} on your list`);
        }
    }
    if (d.thread) { items.push(COPY.conversation); lead.push('this conversation'); }
    if (!hasTargets && !(d.why && d.band)) return null;
    const button = hasTargets ? COPY.button(n) : COPY.buttonWhy(d.band);
    return {
        heading: hasTargets && d.gpa ? COPY.heading(d.gpa) : COPY.headingNoTargets,
        free: hasTargets ? COPY.freeLine(n, sample) : null,
        items,
        button,
        lead: `${cap(joinAnd(lead))}. Free.`,
        payoff: hasTargets && d.why ? 'both' : (hasTargets ? 'targets' : 'why'),
    };
}

/**
 * The strip on the second college page (comparing). `d`:
 *   schools  [{id, short, band?}] this visit's colleges, newest first
 *   gpa      the GPA a read this visit used, or null
 *   targets  {n} from the server for that picture, or null
 */
function stripBox(d) {
    const schools = (d.schools || []).filter((s) => s && s.id && s.short);
    if (schools.length < 2) return null;
    const n = d.targets && Number.isInteger(d.targets.n) ? d.targets.n : null;
    const names = joinAnd(schools.map((s) => s.short));
    return {
        heading: COPY.stripLabel,
        free: n && d.gpa ? COPY.stripTargets(d.gpa, n) : null,
        items: [],
        button: COPY.stripSave,
        lead: COPY.stripLead(names),
        payoff: schools.some((s) => s.band) ? 'bands' : 'figure',
    };
}

// The registry. `entry`: the sign-in entry the moment's button starts
// (content/landingIds.js SIGNIN_ENTRIES and its Python twin). `box(d)`: the
// words for the page's data, or null. `payoffs`: what the server is asked for
// (cloud_functions/profile_manager_v2/signup_moments.PAYOFFS).
export const MOMENTS = {
    chances_result: { entry: 'moment_chances', payoffs: ['fixed_band', 'targets'], box: readBox, notNow: COPY.notNow },
    astra_read: { entry: 'moment_astra_read', payoffs: ['fixed_band', 'targets'], box: readBox, notNow: COPY.notNowAsking },
    // The block halfway down a college page opens the page's one chances
    // card; a read started from it keeps this moment, so its box and the
    // sign-in count as the long read's.
    long_read: { entry: 'moment_long_read', payoffs: ['fixed_band', 'targets'], box: readBox, notNow: COPY.notNow },
    // The strip reads the bands and the count the visit kept; it asks the
    // server nothing.
    comparing: { entry: 'moment_comparing', payoffs: [], box: stripBox, notNow: COPY.notNow },
};

export const momentEntry = (id) => (MOMENTS[id] ? MOMENTS[id].entry : null);
export const MOMENT_ENTRIES = MOMENT_IDS.map((id) => MOMENTS[id].entry);
